import { Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle } from 'lucide-react';
import { FormattedMessage } from 'react-intl';

import { projectQueries } from '@/lib/queries/project';

import { useAuthenticatedUser } from '../providers/AuthenticatedUserProvider';
import { Breadcrumb } from './Breadcrumb';
import { Dropdown } from './DropDown';
import { SheetMenu } from './SheetMenu';

interface NavbarProps {
  isSuperUser: boolean;
}

export const Navbar = ({ isSuperUser }: NavbarProps): JSX.Element => {
  const { user } = useAuthenticatedUser();
  const { data: defaultProject } = useQuery(projectQueries.default());

  if (isSuperUser) {
    return (
      <header className="sticky top-0 z-10 w-full shadow dark:shadow-secondary">
        <div className="bg-indigo-500 dark:bg-indigo-600">
          {/* Warning banner */}
          <div className="flex items-center justify-center py-1 px-4">
            <div className="flex items-center gap-2 text-white">
              <AlertTriangle className="w-3 h-3" />
              <span className="text-sm font-semibold tracking-wide text-xs">
                DEBUG MODE
                {(defaultProject?.defaultProject?.name ?? '') !== '' && (
                  <span className="ml-2 font-normal">
                    - Current project: {defaultProject?.defaultProject?.name}
                  </span>
                )}
              </span>
              <AlertTriangle className="w-3 h-3" />
            </div>
          </div>
        </div>

        {/* Main navbar with enhanced styling for debug mode */}
        <div className="bg-background/95 border-b-2 border-indigo-400 text-muted-foreground dark:border-indigo-600">
          <div className="mx-4 sm:mx-8 flex h-14 items-center gap-2">
            <div className="flex items-center space-x-4 lg:space-x-0">
              <SheetMenu />
              <h1 className="sm:block hidden">
                <Breadcrumb />
              </h1>
            </div>
            <div className="flex flex-1 items-center space-x-2 justify-end gap-4">
              <p className="text-sm font-medium hover:underline">
                <Link to="/my-profile">
                  <FormattedMessage
                    id="global.menu.welcome"
                    values={{ username: user.email }}
                  />
                </Link>
              </p>
              <Dropdown />
            </div>
          </div>
        </div>
      </header>
    );
  }

  // Normal navbar for regular users
  return (
    <header className="sticky top-0 z-10 w-full bg-background/95 shadow backdrop-blur supports-[backdrop-filter]:bg-background/60 dark:shadow-secondary">
      <div className="mx-4 sm:mx-8 flex h-14 items-center gap-2">
        <div className="flex items-center space-x-4 lg:space-x-0">
          <SheetMenu />
          <h1 className="sm:block hidden">
            <Breadcrumb />
          </h1>
        </div>
        <div className="flex flex-1 items-center space-x-2 justify-end gap-4">
          <p className="text-sm text-muted-foreground hover:underline">
            <Link to="/my-profile">
              <FormattedMessage
                id="global.menu.welcome"
                values={{ username: user.email }}
              />
            </Link>
          </p>
          <Dropdown />
        </div>
      </div>
    </header>
  );
};
